import React, { useEffect, useState, memo } from 'react';
import axios from 'axios';
import { Box } from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip
} from 'recharts';
import './LiveUserStatus.css';

// Constants (outside component to avoid re-creation)
const axisTickColor = '#475569';
const gridStrokeColor = '#CBD5E1';
const premiumStrokeColor = '#3B82F6';
const nonPremiumStrokeColor = '#10B981';

const LiveUserStatus = ({ darkMode }) => {
  const [filterType, setFilterType] = useState('month');
  const [stats, setStats] = useState({
    premiumUsers: 0,
    nonPremiumUsers: 0,
    totalUsers: 0,
    premiumRate: 0,
    chartData: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchUserStats = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`/api/v1/get-subscribe-user-count-graph?filterType=${filterType}`, {
          withCredentials: true
        });

        const data = res.data.data || [];
        let totalPremiumUsers = 0;
        let totalUsers = 0;

        const chartData = data.map(item => {
          const premium = item.premium_count || 0;
          const total = item.user_count || 0;
          const nonPremium = total - premium;

          totalPremiumUsers += premium;
          totalUsers += total;

          return {
            day: item.label,
            premium,
            nonPremium: Math.max(0, nonPremium)
          };
        });

        const totalNonPremiumUsers = totalUsers - totalPremiumUsers;
        const premiumRate = totalUsers > 0
          ? Math.round((totalPremiumUsers / totalUsers) * 100)
          : 0;

        if (isMounted) {
          setStats({
            premiumUsers: totalPremiumUsers,
            nonPremiumUsers: totalNonPremiumUsers,
            totalUsers,
            premiumRate,
            chartData
          });
          setError(null);
        }
      } catch (err) {
        if (isMounted) {
          console.error('Error fetching stats:', err);
          setError('Failed to load user stats');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchUserStats();

    return () => {
      isMounted = false;
    };
  }, [filterType]);

  if (loading) return <Box sx={{ p: 3 }}>Loading...</Box>;
  if (error) return <Box sx={{ p: 3, color: 'error.main' }}>{error}</Box>;

  return (
    <Box
      sx={{
        m: '2rem auto',
        p: 3,
        borderRadius: 3,
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
        transition: 'box-shadow 0.3s ease-in-out',
        '&:hover': {
          boxShadow: '0 12px 20px -3px rgba(0, 0, 0, 0.15), 0 6px 10px -4px rgba(0, 0, 0, 0.1)'
        }
      }}
    >
      <LiveUserStatusHeader filterType={filterType} setFilterType={setFilterType} />
      <Box sx={{ height: { xs: 250, sm: 320 }, mb: 4 }}>
        <LiveUserStatusChart data={stats.chartData} />
      </Box>
      <LiveUserStatusStats {...stats} darkMode={darkMode} />
    </Box>
  );
};

const LiveUserStatusHeader = memo(({ filterType, setFilterType }) => (
  <div className="dashboard-header">
    <div className="legend-container">
      <div className="legend-item">
        <div className="legend-dot premium"></div>
        <span className="legend-text">Premium Users</span>
      </div>
      <div className="legend-item">
        <div className="legend-dot non-premium"></div>
        <span className="legend-text">Non-Premium Users</span>
      </div>
    </div>
    <div className="time-selector">
      <select
        className="time-selector-dropdown"
        value={filterType}
        onChange={(e) => setFilterType(e.target.value)}
        aria-label="Time filter"
      >
        <option value="year">Year</option>
        <option value="month">Month</option>
        <option value="week">Week</option>
      </select>
    </div>
  </div>
));

const LiveUserStatusChart = memo(({ data }) => (
  <div className="chart-container">
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={gridStrokeColor} />
        <XAxis
          dataKey="day"
          axisLine={false}
          tickLine={false}
          tick={{ fill: axisTickColor, fontSize: 12 }}
          dy={10}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          tick={{ fill: axisTickColor, fontSize: 12 }}
          domain={[0, 'dataMax + 50']}
        />
        <Tooltip
          contentStyle={{ backgroundColor: '#ffffff', border: 'none', borderRadius: '8px' }}
          labelStyle={{ color: '#111827', fontWeight: 'bold' }}
          itemStyle={{ color: '#1F2937' }}
          formatter={(value, name) => [
            String(value),
            name === 'premium' ? 'Premium Users' : 'Non-Premium Users'
          ]}
        />
        <Line
          type="monotone"
          dataKey="premium"
          stroke={premiumStrokeColor}
          strokeWidth={3}
          dot={{ fill: premiumStrokeColor, r: 4 }}
          activeDot={{ r: 6, stroke: premiumStrokeColor, strokeWidth: 2 }}
        />
        <Line
          type="monotone"
          dataKey="nonPremium"
          stroke={nonPremiumStrokeColor}
          strokeWidth={3}
          dot={{ fill: nonPremiumStrokeColor, r: 4 }}
          activeDot={{ r: 6, stroke: nonPremiumStrokeColor, strokeWidth: 2 }}
        />
      </LineChart>
    </ResponsiveContainer>
  </div>
));

const LiveUserStatusStats = memo(({ premiumUsers, nonPremiumUsers, totalUsers, premiumRate, darkMode }) => {
  const getColorClass = (type) => {
    const suffix = darkMode ? '' : '-light';
    return `${type}-color${suffix}`;
  };

  return (
    <div className="stats-container">
      <div className="stat-card">
        <div className={`stat-value ${getColorClass('premium')}`}>{premiumUsers}</div>
        <div className="stat-label">Premium Users</div>
      </div>
      <div className="stat-card">
        <div className={`stat-value ${getColorClass('non-premium')}`}>{nonPremiumUsers}</div>
        <div className="stat-label">Non-Premium</div>
      </div>
      <div className="stat-card">
        <div className={`stat-value ${getColorClass('total')}`}>{totalUsers}</div>
        <div className="stat-label">Total Users</div>
      </div>
      <div className="stat-card">
        <div className={`stat-value ${getColorClass('premium-rate')}`}>{premiumRate}%</div>
        <div className="stat-label">Premium Rate</div>
      </div>
    </div>
  );
});

export default LiveUserStatus;
